import type { Stream, User } from '$lib/shared/lib/types';
import type { LoadEvent } from '@sveltejs/kit';
import { UserNavigationStates } from './navigation-state';

export type UserRole = 'UNAUTHENTICATED' | 'VIEWER' | 'STREAM_ADMIN' | 'SUPERUSER';

export interface GuardResult {
	allowed: boolean;
	redirect?: string;
	reason?: string;
}

export interface GuardContext {
	user: User | null;
	event?: LoadEvent;
	params?: Record<string, string>;
}

export type UserNavigationState = (typeof UserNavigationStates)[keyof typeof UserNavigationStates];

export type Guard = (context: GuardContext) => GuardResult | Promise<GuardResult>;

export interface RoutePermission {
	path: string;
	public?: boolean;
	requireAuth?: boolean;
	requireNotBanned?: boolean;
	requireActiveStreamer?: boolean;
	requireSuperuser?: boolean;
	requireStreamAccess?: boolean;
	allowedStates?: UserNavigationState[];
	redirectIfAuth?: string;
	redirectIfComplete?: string;
	redirectIfIncomplete?: string;
}

export interface RouteAccessOptions {
	params?: Record<string, string>;
	streams?: Stream[];
}

export interface RouteAccessResult extends GuardResult {
	navigationState?: UserNavigationState;
}

export function isActiveUser(user: User | null): boolean {
	if (!user) return false;
	if (user.superuser) return true;

	return user.streams.some(
		(stream) => stream.stream_state === 'VERIFIED' && stream.stripe_connect_state === 'ENABLED'
	);
}

export function isSuperuser(user: User | null): boolean {
	return user?.superuser ?? false;
}

export function hasStreamAccess(user: User | null, tag: string): boolean {
	if (!user) return false;
	if (user.superuser) return true;

	return user.streams.some((stream) => stream.tag === tag);
}
